import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { Input, notification, Tooltip } from 'antd';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useContextState } from '../../redux/contextState';
import './MapContainer.scss';
import { mapContainerStyle, darkModeStyles } from './mapStyles';
import whiteMarker from '../../assets/map/Marker1.png';
import darkMarker from '../../assets/map/Marker2.png';
import useTranslations from '../../hooks/useTranslations';
import Spinner from '../Spinner/Spinner';
import PinLocationIcon from '../../assets/map/pinLocation';

export default function MapContainer({ onChangeCoordinates, markers }) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.RAZZLE_RUNTIME_GOOGLE_API_KEY,
    libraries: ['places'],
    language: 'es',
  });

  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [defaultCenter, setdefaultCenter] = useState({ lat: -34.6037, lng: -58.3816 });
  const [isLocating, setIsLocating] = useState(false);
  const { contextState } = useContextState();
  const { t } = useTranslations();

  const handlePinUserLocationError = () => {
    notification.open({
      message: t('PREFILTER_LOCATION_PINNED_ERROR'),
      type: 'error',
    });
  };

  const getUserLocation = () => {
    setIsLocating(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = parseFloat(pos.coords.latitude);
          const lng = parseFloat(pos.coords.longitude);
          setdefaultCenter({ lat, lng });
          const newMarker = {
            id: 0,
            position: {
              lat: lat,
              lng: lng,
            },
            label: { text: t('LOCATION_PINNED_ON_MAP'), className: 'map-marker-label' },
          };
          setMarker(newMarker);
          onChangeCoordinates(newMarker);
        },
        () => {
          handlePinUserLocationError();
        },
      );
    } else {
      handlePinUserLocationError();
    }
    setIsLocating(false);
  };

  const onLoad = useCallback(map => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setMarker(null);
  }, []);

  const autocompleteRef = useRef(null);

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry || !place.geometry.location) return;

    const newMarker = {
      id: place.place_id,
      position: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
      label: { text: place.name, className: 'map-marker-label' },
      formatedAddres: place.formatted_address,
    };

    setMarker(newMarker);
    onChangeCoordinates(newMarker);
    map.panTo(newMarker.position);
  };

  useEffect(() => {
    if (map && markers && markers?.position) {
      setMarker(markers);
      map.panTo(markers.position);
    }
  }, [markers, map]);

  return (
    isLoaded && (
      <div className='flex flex-col gap-4'>
        <Autocomplete
          onLoad={autocomplete => {
            autocompleteRef.current = autocomplete;
          }}
          onPlaceChanged={handlePlaceChanged}
          className='map-autocomplete-on'>
          <>
            <div className='map-locate-me'>
              <Input className='flex w-full h-10' type='text' placeholder={t('PREFILTER_LOCATION_SEARCH_ON_MAP')} />
              <Tooltip title={t('LOCATION_PINNED_ON_MAP')} placement='bottom'>
                <button
                  className='w-fit px-5 py-1 mt-1 text-white hover:cursor-pointer disabled:bg-grey disabled:cursor-not-allowed'
                  onClick={getUserLocation}
                  disabled={isLocating}>
                  {isLocating ? <Spinner /> : <PinLocationIcon />}
                </button>
              </Tooltip>
            </div>
          </>
        </Autocomplete>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            streetViewControl: false,
            fullscreenControl: false,
            cameraControl: false,
            mapTypeControl: false,
            styles: contextState?.theme === 'dark' && darkModeStyles,
          }}>
          {marker && (
            <Marker
              key={marker.id}
              position={marker.position}
              label={marker.label}
              icon={{
                url: contextState?.theme === 'dark' ? darkMarker : whiteMarker,
                scaledSize: new window.google.maps.Size(50, 60),
              }}
            />
          )}
        </GoogleMap>
      </div>
    )
  );
}
